import uPlot, { AlignedData } from "uplot";

/**
 * Adds fractional opacity to a CSS oklch color string
 * @param colorString like oklch(59.69% 0.156 49.77)
 * @param opacity in 0.0 -- 1.0
 * @returns modified color string
 */
export function addOKLCHOpacity(colorString: string, opacity: number): string {
   if (!colorString.startsWith('oklch') || colorString.includes('/')) {
      console.warn(`Expected oklch(...) color with no alpha, but got ${colorString}. Not modifying.`)
      return colorString
   }
   return colorString.replace(')', ` / ${opacity})`);
}

export function getChartDimensions(
   container: HTMLElement,
   minHeight: number,
   aspectRatio: number
): { width: number; height: number; } {
   // Get the container's content box width (excluding padding and border)
   const containerStyle = window.getComputedStyle(container);
   const containerWidth = container.clientWidth
      - parseFloat(containerStyle.paddingLeft)
      - parseFloat(containerStyle.paddingRight);

   const width = Math.floor(containerWidth);
   const height = Math.max(minHeight, Math.floor(width / aspectRatio));

   return { width, height };
}

// Shared observer setup functionality
export function setupChartObservers(
   container: HTMLElement,
   chartRef: { current: uPlot | null },
   minHeight: number,
   aspectRatio: number,
   createOptions: (width: number, height: number) => uPlot.Options,
   getData: () => AlignedData,
   setupHandlers: (chart: uPlot) => void,
   onExternalChange?: (callback: () => void) => void
) {
   // Listen for window resize
   const resizeObserver = new ResizeObserver(() => {
      if (chartRef.current) {
         const { width, height } = getChartDimensions(container, minHeight, aspectRatio);
         chartRef.current.setSize({ width, height });
      }
   });

   resizeObserver.observe(container);

   // Watch for theme changes
   const themeObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
         if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
            if (chartRef.current && container) {
               const { width, height } = getChartDimensions(container, minHeight, aspectRatio);
               chartRef.current.destroy();
               chartRef.current = new uPlot(createOptions(width, height), getData(), container);
               setupHandlers(chartRef.current);
            }
         }
      }
   });

   themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
   });

   // Listen for external changes (e.g., slider input)
   if (onExternalChange) {
      onExternalChange(() => {
         if (chartRef.current && container) {
            const { width, height } = getChartDimensions(container, minHeight, aspectRatio);
            chartRef.current.destroy();
            chartRef.current = new uPlot(createOptions(width, height), getData(), container);
            setupHandlers(chartRef.current);
         }
      });
   }
}
